import { itemsApi } from "#/api/itemsApi";
import SelectFile from "#/components/inputs/SelectFile";
import {
  showSuccessNotification,
  showErrorNotification,
} from "#/components/notifications";
import { logger } from "#/utils/logger";
import type { ModalProps } from "@mantine/core";
import {
  Button,
  Divider,
  Drawer,
  Modal,
  Progress as ProgressBar,
  Table,
  Text,
  TextInput,
  Title,
  Badge,
} from "@mantine/core";
import {
  IconDownload,
  IconUpload,
  IconRefresh,
  IconCheck,
  IconX,
  IconEye,
  IconTrash,
} from "@tabler/icons-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import ActionButton from "#/components/buttons/ActionButton";
import BaseTable from "#/components/tables/BaseTable";
import type { Item } from "#/types/items";
import Papa from "papaparse";

interface CsvError {
  row: number;
  data: Record<string, string>;
  error: string;
}

interface ImportResult {
  success: Item[];
  errors: CsvError[];
}

interface PreviewRow {
  rowIndex: number;
  data: Record<string, string>;
}

export interface Props extends ModalProps {
  onSuccess: () => void;
}

const CSV_COLUMNS = [
  { key: "rfid", label: "RFID" },
  { key: "product_sku", label: "Category SKU" },
  { key: "external_id", label: "External ID" },
  { key: "site_name", label: "Site Name" },
  { key: "date_of_circulation", label: "Date of Circulation" },
  { key: "next_maintenance_date", label: "Next Maintenance Date" },
  { key: "last_maintenance_date", label: "Last Maintenance Date" },
  { key: "location", label: "Location" },
  { key: "notify_email", label: "Notify Email" },
];

const ImportCSVModal = ({ onSuccess, onClose, opened }: Props) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const [processing, setProcessing] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [editableErrors, setEditableErrors] = useState<CsvError[]>([]);
  const [retrying, setRetrying] = useState<boolean>(false);

  // Preview state
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [customColumns, setCustomColumns] = useState<string[]>([]);

  const KNOWN_COLUMNS = [
    "RFID",
    "Category SKU",
    "External ID",
    "Site Name",
    "Date of Circulation",
    "Next Maintenance Date",
    "Last Maintenance Date",
    "Location",
    "Notify Email",
  ];

  const parseCSV = (csvFile: File): Promise<PreviewRow[]> => {
    return new Promise((resolve, reject) => {
      Papa.parse(csvFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const rows: PreviewRow[] = [];

          // Detect all headers
          const allHeaders = results.meta.fields || [];

          // Identify custom columns (any header not in KNOWN_COLUMNS)
          const detectedCustomColumns = allHeaders.filter(
            (header) => !KNOWN_COLUMNS.includes(header)
          );
          setCustomColumns(detectedCustomColumns);

          results.data.forEach((row: unknown, index: number) => {
            const record = row as Record<string, string>;
            const mappedData: Record<string, string> = {
              rfid: record["RFID"]?.trim() || "",
              product_sku: record["Category SKU"]?.trim() || "",
              external_id: record["External ID"]?.trim() || "",
              site_name: record["Site Name"]?.trim() || "",
              date_of_circulation: record["Date of Circulation"]?.trim() || "",
              next_maintenance_date:
                record["Next Maintenance Date"]?.trim() || "",
              last_maintenance_date:
                record["Last Maintenance Date"]?.trim() || "",
              location: record["Location"]?.trim() || "",
              notify_email: record["Notify Email"]?.trim() || "",
            };

            // Add custom columns to mappedData (only non-empty values)
            detectedCustomColumns.forEach((customCol) => {
              const value = record[customCol]?.trim() || "";
              if (value) {
                mappedData[customCol] = value;
              }
            });

            const hasData = Object.values(mappedData).some(
              (v) => v && v.length > 0,
            );
            if (hasData) {
              rows.push({ rowIndex: index + 2, data: mappedData });
            }
          });
          resolve(rows);
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  };

  const handleFileSelect = async (selectedFile: File | null) => {
    if (selectedFile) {
      try {
        const rows = await parseCSV(selectedFile);
        setPreviewData(rows);
        if (rows.length > 0) {
          setShowPreview(true);
        } else {
          showErrorNotification({
            message: t("items.import.noValidData"),
          });
        }
      } catch {
        showErrorNotification({
          message: t("items.import.failedToParse"),
        });
      }
    }
  };

  const handlePreviewFieldChange = (
    rowIndex: number,
    field: string,
    value: string,
  ) => {
    setPreviewData((prev) =>
      prev.map((row, idx) =>
        idx === rowIndex
          ? { ...row, data: { ...row.data, [field]: value } }
          : row,
      ),
    );
  };

  const handleRemovePreviewRow = (rowIndex: number) => {
    setPreviewData((prev) => prev.filter((_, idx) => idx !== rowIndex));
  };

  const processData = async () => {
    if (previewData.length === 0) return;

    setProcessing(true);
    setResult(null);

    const allHeaders = [...CSV_COLUMNS.map((col) => col.label), ...customColumns];
    const csvHeader = allHeaders.join(",");
    const csvRows = previewData.map((row) => {
      const knownFields = CSV_COLUMNS.map((col) => row.data[col.key] || "");
      const customFields = customColumns.map((customCol) => row.data[customCol] || "");
      return [...knownFields, ...customFields].join(",");
    });
    const csvContent = [csvHeader, ...csvRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const csvFile = new File([blob], "import.csv", { type: "text/csv" });

    try {
      const response = await itemsApi.uploadCsvFile(csvFile, setProgress);
      setProcessing(false);
      setProgress(0);

      const data = response.data;
      setResult(data);
      setEditableErrors(
        data.errors.map((e) => ({ ...e, data: { ...e.data } })),
      );

      if (data.success.length > 0 && data.errors.length === 0) {
        showSuccessNotification({
          message: t("items.import.importSuccess", { count: data.success.length }),
        });
        onSuccess();
      } else if (data.success.length > 0 && data.errors.length > 0) {
        showSuccessNotification({
          message: t("items.import.importPartial", { success: data.success.length, errors: data.errors.length }),
        });
        onSuccess();
      } else if (data.success.length === 0 && data.errors.length > 0) {
        showErrorNotification({
          message: t("items.import.allFailed", { count: data.errors.length }),
        });
      } else {
        showErrorNotification({
          message: t("items.import.noValidDataFound"),
        });
      }
    } catch (error) {
      logger.error("Process error:", error);
      showErrorNotification({
        message: t("items.import.failedToProcessData"),
      });
      setProcessing(false);
      setProgress(0);
    }
  };

  const handleRetry = async () => {
    if (editableErrors.length === 0) return;

    setRetrying(true);

    const allHeaders = [...CSV_COLUMNS.map((col) => col.label), ...customColumns];
    const csvHeader = allHeaders.join(",");
    const csvRows = editableErrors.map((err) => {
      const knownFields = CSV_COLUMNS.map((col) => err.data[col.key] || "");
      const customFields = customColumns.map((customCol) => err.data[customCol] || "");
      return [...knownFields, ...customFields].join(",");
    });
    const csvContent = [csvHeader, ...csvRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const retryFile = new File([blob], "retry.csv", { type: "text/csv" });

    try {
      const response = await itemsApi.uploadCsvFile(retryFile, setProgress);
      const data = response.data;

      setResult((prev) => ({
        success: [...(prev?.success || []), ...data.success],
        errors: data.errors,
      }));
      setEditableErrors(
        data.errors.map((e) => ({ ...e, data: { ...e.data } })),
      );

      if (data.success.length > 0) {
        showSuccessNotification({
          message: t("items.import.moreImported", { count: data.success.length }),
        });
        onSuccess();
      }

      if (data.errors.length === 0) {
        showSuccessNotification({
          message: t("items.import.allImported"),
        });
      } else if (data.success.length === 0) {
        showErrorNotification({
          message: t("items.import.retryFailed"),
        });
      }
    } catch (error) {
      logger.error("Retry error:", error);
      showErrorNotification({
        message: t("items.import.failedToRetry"),
      });
    } finally {
      setRetrying(false);
      setProgress(0);
    }
  };

  const handleErrorFieldChange = (
    errorIndex: number,
    field: string,
    value: string,
  ) => {
    setEditableErrors((prev) =>
      prev.map((err, idx) =>
        idx === errorIndex
          ? { ...err, data: { ...err.data, [field]: value } }
          : err,
      ),
    );
  };

  const handleDownloadTemplate = useCallback(() => {
    const csvLink = document.createElement("a");
    csvLink.href = "/templates/items_template.csv";
    csvLink.download = "items_template.csv";
    csvLink.click();

    setTimeout(() => {
      const instructionsLink = document.createElement("a");
      instructionsLink.href = "/templates/items_import_instructions.txt";
      instructionsLink.download = "items_import_instructions.txt";
      instructionsLink.click();
    }, 100);
  }, []);

  const handleClose = () => {
    setResult(null);
    setEditableErrors([]);
    setProgress(0);
    setPreviewData([]);
    setShowPreview(false);
    onClose();
  };

  const resetUpload = () => {
    setResult(null);
    setEditableErrors([]);
    setProgress(0);
    setPreviewData([]);
    setShowPreview(false);
  };

  // Build preview table rows
  const previewHeaders = [...CSV_COLUMNS.map((col) => col.label), ...customColumns, t("common.actions")];
  const previewRows = previewData.map((row, idx) => (
    <Table.Tr key={idx}>
      {CSV_COLUMNS.map((col) => (
        <Table.Td key={col.key}>
          <TextInput
            size="xs"
            value={row.data[col.key] || ""}
            disabled={processing}
            onChange={(e) =>
              handlePreviewFieldChange(idx, col.key, e.target.value)
            }
          />
        </Table.Td>
      ))}
      {customColumns.map((customCol) => (
        <Table.Td key={customCol}>
          <TextInput
            size="xs"
            value={row.data[customCol] || ""}
            disabled={processing}
            onChange={(e) =>
              handlePreviewFieldChange(idx, customCol, e.target.value)
            }
          />
        </Table.Td>
      ))}
      <Table.Td>
        <ActionButton
          icon={IconTrash}
          color="red"
          variant="subtle"
          size="sm"
          onClick={() => handleRemovePreviewRow(idx)}
          disabled={processing}
        />
      </Table.Td>
    </Table.Tr>
  ));

  // Build success table rows
  const successHeaders = [t("common.id"), t("common.category"), t("common.site"), t("common.status"), t("common.location")];
  const successRows =
    result?.success.map((item) => (
      <Table.Tr key={item.id}>
        <Table.Td>{item.id}</Table.Td>
        <Table.Td>{item.product?.name || "-"}</Table.Td>
        <Table.Td>{item.site?.name || "-"}</Table.Td>
        <Table.Td>{item.status}</Table.Td>
        <Table.Td>{item.location || "-"}</Table.Td>
      </Table.Tr>
    )) || [];

  // Build error table rows
  const errorHeaders = [t("common.error"), ...CSV_COLUMNS.map((col) => col.label)];
  const errorRows = editableErrors.map((err, idx) => (
    <Table.Tr key={idx}>
      <Table.Td>
        <Text size="xs" c="red">
          {err.error}
        </Text>
      </Table.Td>
      {CSV_COLUMNS.map((col) => (
        <Table.Td key={col.key}>
          <TextInput
            size="xs"
            value={err.data[col.key] || ""}
            disabled={retrying}
            onChange={(e) =>
              handleErrorFieldChange(idx, col.key, e.target.value)
            }
          />
        </Table.Td>
      ))}
    </Table.Tr>
  ));

  // Upload screen (Modal)
  if (!showPreview && !result) {
    return (
      <Modal
        opened={opened}
        onClose={handleClose}
        closeOnClickOutside={!processing}
        closeOnEscape={!processing}
        title={t("items.import.title")}
        size="xl"
        centered
      >
        <div className="w-full flex flex-col gap-6">
          <div className="w-full h-96 gap-4 flex flex-col items-center justify-center">
            <div className="w-full grid grid-cols-[1fr_auto] gap-10">
              <div className="w-full flex flex-col">
                <Title order={6}>{t("items.import.downloadTemplateStep")}</Title>
                <Text size="sm" c="gray.6">
                  {t("items.import.downloadTemplateDescription")}
                </Text>
              </div>
              <Button
                color="black"
                variant="outline"
                leftSection={<IconDownload size={16} />}
                onClick={handleDownloadTemplate}
              >
                {t("items.import.downloadTemplate")}
              </Button>
            </div>
            <div className="w-full">
              <Divider color="gray.3" />
            </div>
            <div className="w-full h-full flex flex-col gap-2">
              <Title order={6}>{t("items.import.uploadStep")}</Title>
              <SelectFile
                accept=".csv,text/csv"
                onFileSelect={handleFileSelect}
              />
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  // Preview screen (Bottom Drawer)
  if (showPreview && !result) {
    return (
      <Drawer
        opened={opened}
        onClose={handleClose}
        title={t("items.import.reviewImportData")}
        position="bottom"
        size="90%"
        closeOnClickOutside={!processing}
        closeOnEscape={!processing}
      >
        <div className="h-full flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge color="blue" size="lg" leftSection={<IconEye size={14} />}>
                {t("items.import.rowsToImport", { count: previewData.length })}
              </Badge>
            </div>
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                color="green"
                leftSection={<IconUpload size={16} />}
                onClick={processData}
                loading={processing}
                disabled={previewData.length === 0 || !canWrite()}
              >
                {t("items.import.processImport")}
              </Button>
            </PermissionTooltip>
          </div>

          {processing && (
            <div className="w-full relative">
              <ProgressBar
                color="green.4"
                size="38"
                value={progress}
                autoContrast
              />
              <span className="font-bold uppercase text-xs text-black/50 absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 z-10">
                {`${progress}%`}
              </span>
            </div>
          )}

          <div className="flex-1 overflow-auto">
            <BaseTable
              headers={previewHeaders}
              rows={previewRows}
              loading={processing}
            />
          </div>
        </div>
      </Drawer>
    );
  }

  // Results screen (Bottom Drawer)
  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      title={t("items.import.importResults")}
      position="bottom"
      size="90%"
      closeOnClickOutside={!retrying}
      closeOnEscape={!retrying}
    >
      <div className="h-full flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {result && result.success.length > 0 && (
              <Badge
                color="green"
                size="lg"
                leftSection={<IconCheck size={14} />}
              >
                {t("items.import.imported", { count: result.success.length })}
              </Badge>
            )}
            {editableErrors.length > 0 && (
              <Badge color="red" size="lg" leftSection={<IconX size={14} />}>
                {t("items.import.errors", { count: editableErrors.length })}
              </Badge>
            )}
          </div>
          <Button
            variant="subtle"
            color="gray"
            onClick={resetUpload}
            disabled={retrying}
          >
            {t("items.import.uploadAnotherFile")}
          </Button>
        </div>

        {result && result.success.length > 0 && (
          <div className="flex flex-col gap-2">
            <Title order={5} c="green">
              {t("items.import.successfullyImportedItems")}
            </Title>
            <BaseTable headers={successHeaders} rows={successRows} />
          </div>
        )}

        {editableErrors.length > 0 && (
          <div className="flex flex-col gap-2 flex-1">
            <div className="flex items-center justify-between">
              <Title order={5} c="red">
                {t("items.import.failedRows")}
              </Title>
              <Button
                color="blue"
                leftSection={<IconRefresh size={16} />}
                onClick={handleRetry}
                loading={retrying}
              >
                {t("items.import.retryFailedRows")}
              </Button>
            </div>
            <div className="flex-1 overflow-auto">
              <BaseTable
                headers={errorHeaders}
                rows={errorRows}
                loading={retrying}
              />
            </div>
          </div>
        )}

        {editableErrors.length === 0 && result && result.success.length > 0 && (
          <div className="flex justify-end">
            <Button color="green" onClick={handleClose}>
              {t("common.done")}
            </Button>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default ImportCSVModal;
